import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ExternalLink, Info, Phone } from "lucide-react";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";

import type { Playbook, PlaybookStep } from "@/components/playbooks/types";
import {
  AppCard,
  AppCardBody,
  AppCheckbox,
  AppText,
  kindredFontFamilies,
} from "../../../ui-foundation";

const STORAGE_KEY = "kindred_playbook_checks";

const DIFFICULTY_TONES: Record<
  string,
  { backgroundColor: string; borderColor: string; textColor: string }
> = {
  straightforward: {
    backgroundColor: "$accentSoft",
    borderColor: "$success",
    textColor: "$success",
  },
  moderate: {
    backgroundColor: "$secondarySoft",
    borderColor: "$accent",
    textColor: "$accent",
  },
  complex: {
    backgroundColor: "$primarySoft",
    borderColor: "$destructive",
    textColor: "$destructive",
  },
};

const getChecked = (playbookId: string): Record<string, boolean> => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return all[playbookId] || {};
  } catch {
    return {};
  }
};

const setChecked = (playbookId: string, checks: Record<string, boolean>) => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    all[playbookId] = checks;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Ignore localStorage failures and keep the UI usable.
  }
};

const PlaybookDetail = ({ playbook }: { playbook: Playbook }) => {
  const steps = useMemo(() => (playbook.steps ?? []) as PlaybookStep[], [playbook.steps]);
  const [expandedStep, setExpandedStep] = useState<number>(steps[0]?.step ?? 1);
  const [checks, setChecks] = useState<Record<string, boolean>>(() => getChecked(playbook.id));
  const [showInfoNote, setShowInfoNote] = useState(false);
  const reviewCadenceLabel =
    playbook.review_frequency_months === 1
      ? "Reviewed monthly"
      : `Reviewed every ${playbook.review_frequency_months} months`;

  const toggleCheck = useCallback(
    (key: string) => {
      setChecks((previous) => {
        const next = { ...previous, [key]: !previous[key] };
        setChecked(playbook.id, next);
        return next;
      });
    },
    [playbook.id],
  );

  const difficultyTone = playbook.difficulty
    ? DIFFICULTY_TONES[playbook.difficulty] ?? null
    : null;

  return (
    <YStack gap="$sm">
      <AppCard>
        <AppCardBody gap="$sm">
          <XStack flexWrap="wrap" alignItems="flex-start" gap="$sm">
            <AppText fontSize="$hero" lineHeight="$hero">
              {playbook.icon}
            </AppText>
            <YStack flex={1} minWidth={220} gap="$xs">
              <XStack flexWrap="wrap" alignItems="center" gap="$xs">
                <AppText fontFamily={kindredFontFamilies.display} fontSize="$xxl" lineHeight="$xxl" color="$heading">
                  {playbook.title}
                </AppText>
                {playbook.difficulty && difficultyTone ? (
                  <XStack
                    alignItems="center"
                    borderWidth={1}
                    borderRadius="$pill"
                    borderColor={difficultyTone.borderColor}
                    backgroundColor={difficultyTone.backgroundColor}
                    paddingHorizontal={10}
                    paddingVertical={4}
                  >
                    <AppText
                      fontSize="$xs"
                      lineHeight="$sm"
                      fontFamily={kindredFontFamilies.sansBold}
                      textTransform="uppercase"
                      letterSpacing={1.2}
                      color={difficultyTone.textColor}
                    >
                      {playbook.difficulty}
                    </AppText>
                  </XStack>
                ) : null}
              </XStack>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                {playbook.description}
              </AppText>
            </YStack>
          </XStack>

          <XStack flexWrap="wrap" gap="$xs">
            {playbook.estimated_time ? (
              <XStack borderWidth={1} borderColor="$borderColor" borderRadius="$pill" backgroundColor="$background" px={12} py={6}>
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                  {playbook.estimated_time}
                </AppText>
              </XStack>
            ) : null}
            <XStack borderWidth={1} borderColor="$borderColor" borderRadius="$pill" backgroundColor="$background" px={12} py={6}>
              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                {steps.length} step{steps.length === 1 ? "" : "s"}
              </AppText>
            </XStack>
            {playbook.last_reviewed_at ? (
              <XStack borderWidth={1} borderColor="$borderColor" borderRadius="$pill" backgroundColor="$background" px={12} py={6}>
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                  Reviewed{" "}
                  {new Date(playbook.last_reviewed_at).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </AppText>
              </XStack>
            ) : null}
            <XStack borderWidth={1} borderColor="$borderColor" borderRadius="$pill" backgroundColor="$background" px={12} py={6}>
              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                {reviewCadenceLabel}
              </AppText>
            </XStack>
          </XStack>
        </AppCardBody>
      </AppCard>

      <AppCard backgroundColor="$panelBackground">
        <TamaguiButton
          unstyled
          type="button"
          width="100%"
          onPress={() => setShowInfoNote((open) => !open)}
          pressStyle={{ opacity: 0.94 }}
        >
          <AppCardBody>
            <XStack alignItems="center" gap="$sm">
              <XStack
                width={32}
                height={32}
                alignItems="center"
                justifyContent="center"
                borderRadius="$pill"
                backgroundColor="$secondarySoft"
              >
                <Info size={15} className="text-secondary" />
              </XStack>
              <YStack flex={1} minWidth={0} gap={2}>
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                  About Kindred support packs
                </AppText>
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                  How this guidance is written, reviewed, and when to get professional advice.
                </AppText>
              </YStack>
              <ChevronDown
                size={16}
                className={showInfoNote ? "rotate-180 text-muted-foreground" : "text-muted-foreground"}
              />
            </XStack>
          </AppCardBody>
        </TamaguiButton>

        {showInfoNote ? (
          <AppCardBody pt={0} gap="$xs">
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              These packs are general information compiled from current public sources and reviewed on a
              regular schedule. They are not legal, financial, medical, or therapeutic advice.
            </AppText>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              Rules, eligibility, and local services can change. Use the official links in each step to
              confirm current details before acting, and get urgent professional help straight away if you or
              a child may be at immediate risk.
            </AppText>
            <Link to="/editorial-policy" className="kindred-inline-link">
              Read the full editorial and advice policy
            </Link>
          </AppCardBody>
        ) : null}
      </AppCard>

      <YStack gap="$sm">
        {steps.map((step) => {
          const isOpen = expandedStep === step.step;

          return (
            <AppCard key={step.step}>
              <TamaguiButton
                unstyled
                type="button"
                width="100%"
                onPress={() => setExpandedStep(isOpen ? -1 : step.step)}
                pressStyle={{ opacity: 0.94 }}
              >
                <AppCardBody>
                  <XStack alignItems="center" gap="$sm">
                    <XStack
                      width={32}
                      height={32}
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="$pill"
                      backgroundColor="$primarySoft"
                    >
                      <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$xs" lineHeight="$sm" color="$primary">
                        {step.step}
                      </AppText>
                    </XStack>
                    <AppText flex={1} fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                      {step.title}
                    </AppText>
                    <ChevronDown
                      size={16}
                      className={isOpen ? "rotate-180 text-muted-foreground" : "text-muted-foreground"}
                    />
                  </XStack>
                </AppCardBody>
              </TamaguiButton>

              {isOpen ? (
                <AppCardBody pt={0} gap="$sm">
                  <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                    {step.description}
                  </AppText>

                  {step.actions.length > 0 ? (
                    <YStack gap="$xs">
                      {step.actions.map((action, index) => {
                        const key = `${step.step}-${index}`;
                        const checked = checks[key] || false;

                        return (
                          <XStack
                            key={key}
                            alignItems="flex-start"
                            gap="$sm"
                            borderWidth={1}
                            borderColor="$borderColor"
                            borderRadius="$md"
                            backgroundColor="$background"
                            paddingHorizontal="$sm"
                            paddingVertical="$sm"
                          >
                            <AppCheckbox
                              checked={checked}
                              onCheckedChange={() => toggleCheck(key)}
                            />
                            <AppText
                              flex={1}
                              color={checked ? "$colorMuted" : "$color"}
                              fontSize="$sm"
                              lineHeight="$md"
                              textDecorationLine={checked ? "line-through" : "none"}
                            >
                              {action}
                            </AppText>
                          </XStack>
                        );
                      })}
                    </YStack>
                  ) : null}

                  {step.call_script ? (
                    <AppCard backgroundColor="$primarySoft" borderColor="$primary">
                      <AppCardBody gap="$xs">
                        <XStack alignItems="center" gap="$xs">
                          <Phone size={13} className="text-primary" />
                          <AppText
                            color="$primary"
                            fontFamily={kindredFontFamilies.sansBold}
                            fontSize="$xs"
                            lineHeight="$sm"
                            textTransform="uppercase"
                            letterSpacing={1.2}
                          >
                            What To Say
                          </AppText>
                        </XStack>
                        <AppText fontSize="$sm" lineHeight="$md">
                          &quot;{step.call_script}&quot;
                        </AppText>
                      </AppCardBody>
                    </AppCard>
                  ) : null}

                  {step.tip ? (
                    <AppCard backgroundColor="$accentSoft" borderColor="$accent">
                      <AppCardBody>
                        <AppText fontSize="$sm" lineHeight="$md">
                          {"💡 "}{step.tip}
                        </AppText>
                      </AppCardBody>
                    </AppCard>
                  ) : null}

                  {step.links?.length ? (
                    <XStack flexWrap="wrap" gap="$xs">
                      {step.links.map((link) => (
                        <a
                          key={`${step.step}-${link.url}`}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none" }}
                        >
                          <XStack
                            alignItems="center"
                            gap={6}
                            borderWidth={1}
                            borderColor="$secondary"
                            borderRadius={999}
                            backgroundColor="$secondarySoft"
                            paddingHorizontal={12}
                            paddingVertical={8}
                          >
                            <AppText color="$secondary" fontSize="$xs" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                              {link.label}
                            </AppText>
                            <ExternalLink size={11} className="text-secondary" />
                          </XStack>
                        </a>
                      ))}
                    </XStack>
                  ) : null}
                </AppCardBody>
              ) : null}
            </AppCard>
          );
        })}
      </YStack>
    </YStack>
  );
};

export default PlaybookDetail;
